import { Session } from '@benzinga/session';
import { Subscribable, SubscribableEventType, Subscription } from '@benzinga/subscribable';
import { RefreshInterval, ScannerConfig } from '@benzinga/scanner-config-manager';
import { ScannerSocket } from './socket';
import { QuoteProtos, ScannerProtos } from './scanner_protos';
import { configToQuery } from './utils';
import { delta, getNextQuotes } from './compare';
import { ScannerRequest } from './request';
import { QuotesV3FieldsManager } from '@benzinga/quotes-v3-fields-manager';
import { oneRequestAtATime } from '@benzinga/utils';

interface ScannerFeedRowUpdateEvent {
  rows: QuoteProtos.IQuote[];
  config: ScannerConfig;
  totalInstruments: number;
  transactions: ScannerProtos.IRowUpdatesV2;
  type: 'data_update';
}

export type ScannerFeedEvent =
  | ScannerFeedRowUpdateEvent
  | Exclude<SubscribableEventType<ScannerSocket>, { type: 'row_update' }>;

const isRealTime = (interval: number) => interval === RefreshInterval['Real-time'];

export class ScannerFeed extends Subscribable<ScannerFeedEvent> {
  private config: ScannerConfig;
  private session: Session;
  private request: ScannerRequest;

  /** Only created and used when refreshInterval is Real-time - prevents REST requests from touching WebSocket */
  private socket?: ScannerSocket;
  private socketSubscription?: Subscription<ScannerSocket>;
  private refreshTimer?: ReturnType<typeof setInterval>;

  private getQuotesOneAtATime = oneRequestAtATime(() => this.getQuotes());
  private previousQuotes: QuoteProtos.IQuote[] = [];
  /** Next row_update is a full session snapshot relative to empty state (reconnect or new socket). */
  private pendingReconnectSnapshot = false;
  private fireOnZeroSubscription: () => void;

  constructor(session: Session, config: ScannerConfig, request: ScannerRequest, onZeroSubscriptions: () => void) {
    super();
    this.config = config;
    this.session = session;
    this.request = request;
    this.fireOnZeroSubscription = onZeroSubscriptions;

    const sub = this.session.getManager(QuotesV3FieldsManager).subscribe(event => {
      switch (event.type) {
        case 'quotes_fields:update':
          // Fields can arrive after the feed is already live. Replace the socket only when
          // subscribed — and always tear down the previous one first so we do not stack WS
          // connections while the old close is still in flight.
          if (isRealTime(this.config.refreshInterval) && this.socketSubscription) {
            this.replaceRealtimeSocket();
          }
          this.refresh();
      }
    });

    session.onPreSessionClose(() => sub.unsubscribe());
  }

  public refresh = () => {
    switch (this.config.refreshInterval) {
      case RefreshInterval['Real-time']:
        break;
      case RefreshInterval['Never']:
      case RefreshInterval['10s']:
      case RefreshInterval['1m']:
      case RefreshInterval['30s']:
      default:
        this.getQuotesOneAtATime();
        break;
    }
  };

  public getConfig = () => this.config;
  public getPrevious = () => this.previousQuotes;

  protected onFirstSubscription() {
    if (isRealTime(this.config.refreshInterval)) {
      this.replaceRealtimeSocket();
      return;
    }
    this.startPollingMode();
  }

  protected onZeroSubscriptions() {
    this.teardownRealtimeSocket();
    this.clearRefreshTimer();
    this.previousQuotes = [];
    this.pendingReconnectSnapshot = false;
    this.fireOnZeroSubscription();
  }

  private startPollingMode = (): void => {
    switch (this.config.refreshInterval) {
      case RefreshInterval['Never']:
        this.getQuotesOneAtATime();
        break;
      case RefreshInterval['10s']:
      case RefreshInterval['1m']:
      case RefreshInterval['30s']:
      default:
        this.getQuotesOneAtATime();
        this.refreshTimer = setInterval(() => this.getQuotesOneAtATime(), this.config.refreshInterval * 1000);
        break;
    }
  };

  private clearRefreshTimer = (): void => {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  };

  private replaceRealtimeSocket = (): void => {
    this.teardownRealtimeSocket();
    // New sessions (fields refresh or first subscribe) send SNAPSHOT_DELTAS from empty
    // state — same as post-reconnect. Apply the first row_update from [] so we do not
    // merge snapshot adds onto stale previousQuotes.
    this.pendingReconnectSnapshot = true;
    this.socket = new ScannerSocket(this.session, {
      query: configToQuery(this.config, this.session).ok ?? { limit: 1 },
      updateType: ScannerProtos.ScannerConfig.UpdateType.SNAPSHOT_DELTAS,
    });
    this.socketSubscription = this.socket.subscribe(this.onScannerMessage);
  };

  private teardownRealtimeSocket = (): void => {
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.socket = undefined;
  };

  private onScannerMessage = (event: SubscribableEventType<ScannerSocket>) => {
    switch (event.type) {
      case 'on-status-change':
        this.dispatch(event);
        if (event.status === 'reconnected') {
          // Keep previousQuotes / do not emit a remove-all data_update. Wiping here blanks
          // the grid until the post-reconnect snapshot arrives (same empty-grid flash signals
          // avoided). The new session still sends SNAPSHOT_DELTAS relative to empty state —
          // apply that next row_update from [] without an intermediate blank publish.
          this.pendingReconnectSnapshot = true;
        }
        break;
      case 'row_update': {
        const baseQuotes = this.pendingReconnectSnapshot ? [] : this.previousQuotes;
        this.pendingReconnectSnapshot = false;
        this.previousQuotes = getNextQuotes(baseQuotes, event.rows);
        this.dispatch({
          config: this.config,
          rows: this.previousQuotes,
          totalInstruments: event.rows.total ?? 0,
          transactions: event.rows,
          type: 'data_update',
        });
        break;
      }
      default:
        this.dispatch(event);
        break;
    }
  };

  private getQuotes = async () => {
    const quotes = await this.request.getScannerInstruments(this.config);
    if (quotes.ok) {
      const prevQuotes = this.previousQuotes;
      this.previousQuotes = quotes.ok.instruments ?? [];
      this.dispatch({
        config: this.config,
        rows: this.previousQuotes,
        totalInstruments: quotes.ok.totalInstruments ?? 0,
        transactions: delta(prevQuotes, this.previousQuotes, this.session),
        type: 'data_update',
      });
    } else {
      this.previousQuotes = [];
      this.dispatch({
        config: this.config,
        rows: [],
        totalInstruments: 0,
        transactions: {
          adds: [],
          moves: [],
          removes: this.previousQuotes.map((_, idx) => ({ previousIndex: idx })),
          updates: [],
        },
        type: 'data_update',
      });
    }
  };
}
